import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Form, Input, Typography, message } from 'antd';
import { WechatOutlined } from '@ant-design/icons';
import useAuthStore from '../stores/authStore';
import SmsCodeInput from '../components/SmsCodeInput';
import client from '../api/client';
import { exchangeWechatLogin, bindWechatPhone } from '../api/wechat';

const { Title, Text } = Typography;

/**
 * F-07 微信授权回调页（公开路由 /wechat-callback）
 * 后端回调 302 到本页携带 result（base64 JSON）：
 * - type=login → 兑换 Token 直接登录
 * - type=bind  → 展示绑定手机号表单（短信验证）→ 绑定并登录
 */
export default function WechatCallback() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const login = useAuthStore((s) => s.login);
  const [status, setStatus] = useState<'loading' | 'bind' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [bindTicket, setBindTicket] = useState<string | null>(null);
  const [nickname, setNickname] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const raw = search.get('result');
    if (!raw) {
      setStatus('error');
      setErrorMsg('缺少回调参数，请重新扫码');
      return;
    }
    try {
      // base64 → utf8 JSON
      const json = decodeURIComponent(escape(window.atob(raw)));
      const result = JSON.parse(json);
      if (result.type === 'login') {
        exchangeWechatLogin(result.loginTicket)
          .then(({ user, token }) => {
            login(user, token);
            message.success('微信登录成功');
            navigate('/', { replace: true });
          })
          .catch((e) => {
            setStatus('error');
            setErrorMsg(e?.response?.data?.message || '微信登录失败，请重试');
          });
      } else if (result.type === 'bind') {
        setBindTicket(result.bindTicket);
        setNickname(result.profile?.nickname || '');
        setStatus('bind');
      } else if (result.type === 'bind_account') {
        // 已登录绑定模式：刷新当前用户资料后回个人中心
        client
          .get('/auth/me')
          .then(({ data }) => useAuthStore.getState().setUser(data))
          .catch(() => undefined)
          .finally(() => {
            message.success('微信绑定成功');
            navigate('/account', { replace: true });
          });
      } else if (result.type === 'error') {
        setStatus('error');
        setErrorMsg(result.message || '操作失败，请重试');
      } else {
        setStatus('error');
        setErrorMsg('回调数据异常');
      }
    } catch {
      setStatus('error');
      setErrorMsg('回调数据解析失败，请重新扫码');
    }
  }, [search, login, navigate]);

  const handleBind = async () => {
    if (!bindTicket) return;
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      message.warning('请输入正确的手机号');
      return;
    }
    if (!code || code.length < 4) {
      message.warning('请输入短信验证码');
      return;
    }
    setBusy(true);
    try {
      const { user, token } = await bindWechatPhone({ bindTicket, phoneNumber: phone, smsCode: code });
      login(user, token);
      message.success('微信绑定成功，已登录');
      if (user?.hasPassword === false) {
        message.info('账号尚未设置密码，建议前往个人中心设置，之后可用手机号+密码登录', 6);
      }
      navigate('/', { replace: true });
    } catch (e: any) {
      message.error(e?.response?.data?.message || '绑定失败，请重试');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f0f1a' }}>
      <Card style={{ width: 420, background: '#1a1a2e', borderColor: '#333' }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <WechatOutlined style={{ fontSize: 40, color: '#07c160' }} />
          <Title level={4} style={{ margin: '8px 0 0', color: '#fff' }}>微信登录</Title>
        </div>

        {status === 'loading' && <Text style={{ color: '#aaa', display: 'block', textAlign: 'center' }}>登录中，请稍候…</Text>}

        {status === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <Text type="danger" style={{ display: 'block', marginBottom: 12 }}>{errorMsg}</Text>
            <Button type="primary" onClick={() => navigate('/login', { replace: true })}>返回登录</Button>
          </div>
        )}

        {status === 'bind' && (
          <Form layout="vertical">
            <Text style={{ color: '#aaa', display: 'block', marginBottom: 12 }}>
              {nickname ? `微信「${nickname}」` : '该微信'}未绑定账号：请输入手机号完成登录。
              已注册手机号将绑定到原账号；未注册将自动创建新账号（手机号为主账号）。
            </Text>
            <Form.Item label="手机号" required>
              <Input placeholder="请输入手机号" maxLength={11} value={phone} onChange={(e) => setPhone(e.target.value)} />
            </Form.Item>
            <Form.Item label="短信验证码" required>
              <SmsCodeInput scene="login" phoneNumber={phone} value={code} onChange={setCode} />
            </Form.Item>
            <Button type="primary" block loading={busy} onClick={handleBind}>
              绑定并登录
            </Button>
          </Form>
        )}
      </Card>
    </div>
  );
}